import { useEffect } from "react";
import markerIconSvg from "./marker.svg";
import { useGoogleMaps } from "./useGoogleMaps";

interface Location {
  id: string;
  name: string;
  deletedAt?: string | null;
  address: {
    coordinates: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface LocationsMapProps {
  defaultAreaCoverageInMeters?: number;
  locations: Location[];
  selectedLocationId?: string;
  onLocationSelect?: (locationId: string) => void;
  style?: React.CSSProperties;
}

export function LocationsMap({
  defaultAreaCoverageInMeters,
  locations,
  selectedLocationId,
  onLocationSelect,
  style = { height: "300px", width: "100%" }
}: LocationsMapProps) {
  const [mapContainerRef$, mapInstance] = useGoogleMaps(() => {
    return createBoundsFromLocations(locations);
  })

  useEffect(() => {
    if (!mapInstance || locations.length < 1) {
      return;
    }

    // Create markers on map
    const markers = createMarkers(mapInstance, locations, selectedLocationId, onLocationSelect);
    // Create circles for each location
    const circles = defaultAreaCoverageInMeters !== undefined && defaultAreaCoverageInMeters > 0
      ? createCircles(mapInstance, locations, defaultAreaCoverageInMeters)
      : [];

    if (selectedLocationId !== undefined) {
      const selected = locations.find(x => x.id === selectedLocationId);

      if (selected !== undefined) {
        mapInstance.setCenter({
          lat: selected.address.coordinates.latitude,
          lng: selected.address.coordinates.longitude,
        });

        mapInstance.setZoom(15);
      }
    }

    return function cleanup() {
      markers.forEach(marker => {
        marker.map = null;
      });

      circles.forEach(circle => {
        circle?.setMap(null);
      });
    }
  }, [locations, mapInstance, selectedLocationId, onLocationSelect, defaultAreaCoverageInMeters]);

  return (
    <div
      ref={mapContainerRef$}
      style={style}
    />
  );
}

function createCircles(mapInstance: google.maps.Map, locations: Location[], defaultAreaCoverageInMeters: number) {
  return locations.map(location => new google.maps.Circle({
    map: mapInstance,
    center: {
      lat: location.address.coordinates.latitude,
      lng: location.address.coordinates.longitude,
    },
    radius: defaultAreaCoverageInMeters,
    fillColor: 'rgb(15, 157, 88)',
    fillOpacity: 0.1,
    strokeColor: 'rgb(15, 157, 88)',
    strokeOpacity: 1.0,
    strokeWeight: 2,
  }));
}

function createCustomMarkerElement(isSelected: boolean) {
  const markerElement = document.createElement('div');
  markerElement.style.cssText = `
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: absolute;
    transform: translate(-16px, -16px);
    border-radius: 50%;
    border: ${isSelected ? '5px solid #0f9d58' : '1px solid white'};
    background-color: ${isSelected ? '#ffffff' : 'transparent'};
    box-shadow: ${isSelected ? '0 2px 8px rgba(15, 157, 88, 0.3)' : 'none'};
  `;

  const iconImg = document.createElement('img');
  iconImg.src = markerIconSvg;
  iconImg.style.cssText = `
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: ${isSelected ? 'none' : 'grayscale(0.2)'};
  `;

  markerElement.appendChild(iconImg);
  return markerElement;
}

function createMarkers(mapInstance: google.maps.Map, locations: Location[], selectedId: string | undefined, onClick?: (id: string) => void) {
  return locations.map(location => {
    const isSelected = location.id === selectedId;
    const customMarkerElement = createCustomMarkerElement(isSelected);

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: {
        lat: location.address.coordinates.latitude,
        lng: location.address.coordinates.longitude,
      },
      gmpClickable: true,
      map: mapInstance,
      title: location.name,
      content: customMarkerElement,
      gmpDraggable: false,
    });

    marker.onclick = () => {
      onClick?.(location.id);
    };

    return marker;
  })
}

function createBoundsFromLocations(locations: Location[]) {
  const mapBounds = new google.maps.LatLngBounds();

  locations.forEach(location => {
    mapBounds.extend({
      lat: location.address.coordinates.latitude,
      lng: location.address.coordinates.longitude,
    });
  });

  return mapBounds;
}
